import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { chatActions } from '../../actions/chat/chatActions.js';
import ChatConversation from './ChatConversation.js';
import ChatConversationList from './ChatConversationList.js';
import NewConversationModal from './NewConversationModal.js';
import MessageIcon from '@mui/icons-material/Message';
import CloseIcon from '@mui/icons-material/Close';
import CreateIcon from '@mui/icons-material/Create';

const FloatingButton = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(102, 126, 234, 0.6);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const BadgeCount = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  background: linear-gradient(135deg, #ff4757 0%, #ff6348 100%);
  color: white;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  border: 3px solid white;
  box-shadow: 0 2px 8px rgba(255, 71, 87, 0.4);
  animation: ${props => props.hasUnread ? 'pulse 2s infinite' : 'none'};
  
  @keyframes pulse {
    0% {
      box-shadow: 0 2px 8px rgba(255, 71, 87, 0.4);
    }
    50% {
      box-shadow: 0 2px 12px rgba(255, 71, 87, 0.8);
    }
    100% {
      box-shadow: 0 2px 8px rgba(255, 71, 87, 0.4);
    }
  }
`;

const AnnouncementBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
  color: white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  border: 2px solid white;
  animation: pulse-announcement 2s infinite;
  
  @keyframes pulse-announcement {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.2);
    }
  }
`;

const ChatSidebar = styled.div`
  position: fixed;
  bottom: 90px;
  right: 20px;
  width: 900px;
  height: 650px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  z-index: 998;
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 1200px) {
    width: 750px;
  }

  @media (max-width: 768px) {
    width: calc(100vw - 40px);
    height: 70vh;
    right: 20px;
    left: 20px;
  }
`;

const ChatHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  color: #333;
`;

const ChatTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 700;
  font-size: 16px;

  svg {
    font-size: 24px;
    color: #667eea;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const NewMessageButton = styled.button`
  background: #667eea;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: #5568d3;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: #f0f0f0;
    color: #333;
  }
`;

const ChatContainer = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const ConversationListPanel = styled.div`
  width: 300px;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  min-height: 0;

  @media (max-width: 768px) {
    width: 100%;
    border-right: none;
  }
`;

const ConversationViewPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;

  @media (max-width: 768px) {
    display: ${props => props.show ? 'flex' : 'none'};
  }
`;

function ChatWidget(props) {
  const { 
    isOpen, 
    conversations, 
    activeConversationId, 
    unreadCount,
    fetching,
    getConversations,
    toggleChatOpen,
    setActiveConversation,
    getMessages,
    getUnreadCount,
    user
  } = props;

  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const isTypingRef = useRef(false);
  const typingTimeoutRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const unreadPollIntervalRef = useRef(null);

  useEffect(() => {
    // Always poll for unread count (even when chat is closed)
    // This keeps the notification badge updated
    unreadPollIntervalRef.current = setInterval(() => {
      getUnreadCount();
    }, 5000); // Poll unread count every 5 seconds

    return () => {
      if (unreadPollIntervalRef.current) {
        clearInterval(unreadPollIntervalRef.current);
        unreadPollIntervalRef.current = null;
      }
    };
  }, [getUnreadCount]);

  useEffect(() => {
    if (isOpen) {
      // Fetch conversations when opening chat
      getConversations();
      
      // Set up polling for new messages - but skip if typing
      pollIntervalRef.current = setInterval(() => {
        if (!isTypingRef.current) {
          getConversations();
          if (activeConversationId) {
            getMessages(activeConversationId);
          }
        }
      }, 3000); // Poll every 3 seconds
    } else {
      // Clear polling when chat widget closes
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }

    return () => {
      // Cleanup: clear interval on unmount or when isOpen changes to false
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isOpen, activeConversationId]);

  const handleToggleChat = () => {
    toggleChatOpen(!isOpen);
  };

  const handleSelectConversation = (conversationId) => {
    setActiveConversation(conversationId);
    getMessages(conversationId);
  };

  const handleBackToList = () => {
    setActiveConversation(null);
  };

  const handleUserTyping = () => {
    isTypingRef.current = true;
    
    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Resume polling after 2 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      isTypingRef.current = false;
    }, 2000);
  };

  const handleInputFocus = () => {
    isTypingRef.current = true;
  };

  const handleInputBlur = () => {
    isTypingRef.current = false;
  };

  // Check if there are unread announcements
  const hasUnreadAnnouncements = conversations.some(
    conv => conv.isAnnouncementChannel && conv.unreadCount > 0
  );

  return (
    <>
      {/* Floating Button */}
      <FloatingButton onClick={handleToggleChat} title="Chat">
        <MessageIcon />
        {unreadCount > 0 && <BadgeCount hasUnread={true}>{unreadCount > 99 ? '99+' : unreadCount}</BadgeCount>}
        {hasUnreadAnnouncements && <AnnouncementBadge>📢</AnnouncementBadge>}
      </FloatingButton>

      {/* Chat Sidebar */}
      {isOpen && (
        <ChatSidebar>
          <ChatHeader>
            <ChatTitle>
              <MessageIcon />
              Chats
            </ChatTitle>
            <HeaderActions>
              <NewMessageButton onClick={() => setShowNewConversationModal(true)}>
                <CreateIcon fontSize="small" />
                New Message
              </NewMessageButton>
              <CloseButton onClick={handleToggleChat}>
                <CloseIcon fontSize="small" />
              </CloseButton>
            </HeaderActions>
          </ChatHeader>
          
          <ChatContainer>
            <ConversationListPanel>
              <ChatConversationList 
                conversations={conversations}
                onSelectConversation={handleSelectConversation}
                isFetching={fetching.conversations}
                activeConversationId={activeConversationId}
              />
            </ConversationListPanel>

            {activeConversationId && (
              <ConversationViewPanel show={true}>
                <ChatConversation 
                  conversationId={activeConversationId}
                  onBack={handleBackToList}
                  onUserTyping={handleUserTyping}
                  onInputFocus={handleInputFocus}
                  onInputBlur={handleInputBlur}
                />
              </ConversationViewPanel>
            )}
          </ChatContainer>

          {showNewConversationModal && (
            <NewConversationModal 
              onClose={() => setShowNewConversationModal(false)}
              onSelectUser={(userId) => {
                // This will be handled by the modal component
              }}
            />
          )}
        </ChatSidebar>
      )}
    </>
  );
}

const mapStateToProps = (state) => {
  return {
    isOpen: state.chat.isOpen,
    conversations: state.chat.conversations,
    activeConversationId: state.chat.activeConversationId,
    unreadCount: state.chat.unreadCount,
    fetching: state.chat.fetching,
    user: state.users.user.user
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    toggleChatOpen: (isOpen) => dispatch(chatActions.toggleChatOpen(isOpen)),
    setActiveConversation: (conversationId) => dispatch(chatActions.setActiveConversation(conversationId)),
    getConversations: () => dispatch(chatActions.getConversations()),
    getMessages: (conversationId) => dispatch(chatActions.getMessages(conversationId)),
    getUnreadCount: () => dispatch(chatActions.getUnreadCount()),
    sendMessage: (conversationId, message) => dispatch(chatActions.sendMessage(conversationId, message))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ChatWidget);
